import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EVENT_CATEGORY_CODES,
  EVENT_CATEGORY_META,
  ROLES,
  type EventCategory,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, ErrorNote, Field, FieldGroup, Input, Textarea } from '@/components/ui';
import { api, apiData } from '@/lib/api';
import { todayISO } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ClassRoomSummary } from '@/types';

/** 일정 등록 — 명세서 §5.6 */
export function EventEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useAuth((s) => s.membership);
  const isDirector = membership?.role === ROLES.DIRECTOR;

  const { data: rooms } = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<EventCategory>('FESTIVAL');
  // 교사는 전체 일정을 만들 수 없으므로(§3.2) 기본값을 담임 반으로 둔다.
  const [classRoomId, setClassRoomId] = useState<string>('');
  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [supplies, setSupplies] = useState('');
  const [remindDayBefore, setRemindDayBefore] = useState(false);

  const effectiveRoomId = isDirector ? classRoomId : classRoomId || (rooms?.[0]?.id ?? '');

  const save = useMutation({
    mutationFn: () =>
      api('/events', {
        method: 'POST',
        body: {
          title,
          category,
          classRoomId: effectiveRoomId || undefined,
          startAt: `${date}T00:00:00+09:00`,
          description: description || undefined,
          supplies: supplies || undefined,
          remindDayBefore,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/schedule');
    },
  });

  return (
    <Screen withNav={false}>
      <AppBar title="일정 등록" back />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <Field label="제목" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 봄 소풍" />
          </Field>

          <FieldGroup label="분류">
            <div className="flex flex-wrap gap-2">
              {EVENT_CATEGORY_CODES.map((code) => (
                <Chip
                  key={code}
                  tone={code === category ? 'primary' : 'neutral'}
                  selected={code === category}
                  onClick={() => setCategory(code)}
                >
                  {EVENT_CATEGORY_META[code].label}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup
            label="대상"
            hint={isDirector ? '반을 고르지 않으면 원 전체 일정이에요.' : '담임 반에만 등록할 수 있어요.'}
          >
            <div className="flex flex-wrap gap-2">
              {isDirector && (
                <Chip
                  tone={classRoomId === '' ? 'primary' : 'neutral'}
                  selected={classRoomId === ''}
                  onClick={() => setClassRoomId('')}
                >
                  원 전체
                </Chip>
              )}
              {rooms?.map((room) => (
                <Chip
                  key={room.id}
                  tone={room.id === effectiveRoomId ? 'primary' : 'neutral'}
                  selected={room.id === effectiveRoomId}
                  onClick={() => setClassRoomId(room.id)}
                >
                  {room.name}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <Field label="날짜" required>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>

          <Field label="안내">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>

          {/* 리마인드 안내는 아래 "전날 준비물 알림 보내기" 체크박스가 이미 말한다 — 중복 설명을 두지 않는다 */}
          <Field label="준비물">
            <Input
              value={supplies}
              onChange={(e) => setSupplies(e.target.value)}
              placeholder="예: 도시락, 물통, 돗자리"
            />
          </Field>

          <label className="mt-1 flex items-center gap-2 text-base">
            <input
              type="checkbox"
              checked={remindDayBefore}
              disabled={!supplies}
              onChange={(e) => setRemindDayBefore(e.target.checked)}
              className="h-5 w-5 accent-[color:var(--color-primary)]"
            />
            전날 준비물 알림 보내기
          </label>
        </Card>

        {save.isError && <ErrorNote message={(save.error as Error).message} />}

        <Button block disabled={!title} loading={save.isPending} onClick={() => save.mutate()}>
          등록
        </Button>
      </div>
    </Screen>
  );
}
